import { useState, useEffect } from 'react';
import { History as HistoryIcon, Zap, TrendingUp, TrendingDown, Activity, Filter, Download, Calendar, Search, BarChart2, X, Clock, DollarSign, Target, ArrowUp, ArrowDown, Info, CheckCircle, XCircle, Copy, Check } from 'lucide-react';
import { tradingAPI, arbitrageAPI, binaryAPI } from '../../services/api';
import CryptoIcon from '../CryptoIcon';
import { formatPair, getCommodity } from '../../utils/commodities';
import { toast } from 'sonner';

import { fmtFixed, fmtUsd, fmtPrice } from '../../utils/format';
// Trade Summary Report Modal Component - Enhanced Design
const TradeDetailModal = ({ trade, onClose }) => {
  const [copied, setCopied] = useState(false);
  
  if (!trade) return null;
  
  // Binary uses status='won'/'lost'. Arbitrage uses status='completed' but can be a loss via admin force-outcome.
  // Treat as win when: binary won, OR (completed AND positive profit), OR explicit win outcome.
  const profit = trade.actual_profit ?? trade.actual_return ?? trade.profit ?? 0;
  const isWin =
    trade.status === 'won' ||
    trade.outcome === 'win' ||
    trade.outcome === 'win_full' ||
    (trade.status === 'completed' && profit > 0);
  const isBinary = trade.type === 'binary';
  const isArbitrage = trade.type === 'arbitrage';
  const summary = trade.summary_report || {};
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit', 
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
    } catch {
      return dateString;
    }
  };
  
  const formatMoney = (amount) => {
    if (!amount && amount !== 0) return '$0.00';
    const num = parseFloat(amount);
    return `$${Math.abs(num).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  
  const getDurationText = () => {
    const seconds = trade.duration_seconds || summary.duration_seconds || 60;
    if (seconds >= 3600) return `${Math.floor(seconds / 3600)} Hour${seconds >= 7200 ? 's' : ''}`;
    if (seconds >= 60) return `${Math.floor(seconds / 60)} Minute${seconds >= 120 ? 's' : ''}`;
    return `${seconds} Second${seconds !== 1 ? 's' : ''}`;
  };

  const copyTradeId = () => {
    const id = summary.trade_id || trade.id;
    navigator.clipboard.writeText(id);
    setCopied(true);
    toast.success('Trade ID copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  // Calculate values
  const investmentAmount = summary.amount_usdt || trade.amount || 0;
  const profitRate = (summary.profit_rate || trade.profit_rate || 0.85) * 100;
  const profitLoss = summary.actual_profit || trade.actual_profit || 0;
  const totalCredited = isWin ? investmentAmount + profitLoss : 0;
  const direction = (summary.prediction || trade.prediction || 'up').toUpperCase();
  const tradeId = (summary.trade_id || trade.id || '').substring(0, 11);
  const pair = summary.trading_pair || formatPair(trade.crypto_symbol || 'BTC');
  
  return (
    <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-2" onClick={onClose}>
      <div 
        className="bg-[#0a1628] rounded-2xl max-w-sm w-full shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Win/Loss Banner - Compact */}
        <div className={`p-4 rounded-t-2xl ${isWin ? 'bg-gradient-to-br from-green-600 to-green-700' : 'bg-gradient-to-br from-red-600 to-red-700'}`}>
          <div className="flex items-center justify-center space-x-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isWin ? 'bg-green-500/30' : 'bg-red-500/30'}`}>
              {isWin ? (
                <CheckCircle className="h-6 w-6 text-white" />
              ) : (
                <XCircle className="h-6 w-6 text-white" />
              )}
            </div>
            <div className="text-center">
              <h2 className="text-lg font-bold text-white">Trade {isWin ? 'Won' : 'Lost'}</h2>
              <p className="text-2xl font-bold text-white">
                {isWin ? '+' : '-'}{formatMoney(Math.abs(profitLoss))}
              </p>
            </div>
          </div>
        </div>
        
        {/* Trade Details List - Compact */}
        <div className="px-4 py-2">
          {/* Row 1: Trade ID & Pair */}
          <div className="flex justify-between py-2 border-b border-gray-700/50">
            <div className="flex-1">
              <span className="text-gray-500 text-xs">Trade ID</span>
              <div className="flex items-center">
                <span className="text-white text-sm font-medium">{tradeId}</span>
                <button onClick={copyTradeId} className="ml-1 p-0.5">
                  {copied ? <Check className="h-3 w-3 text-green-400" /> : <Copy className="h-3 w-3 text-gray-400" />}
                </button>
              </div>
            </div>
            <div className="flex-1 text-right">
              <span className="text-gray-500 text-xs">Pair</span>
              <p className="text-white text-sm font-medium">{pair}</p>
            </div>
          </div>
          
          {/* Row 2: Direction & Duration */}
          <div className="flex justify-between py-2 border-b border-gray-700/50">
            <div className="flex-1">
              <span className="text-gray-500 text-xs">Direction</span>
              <p className={`text-sm font-bold ${direction === 'UP' ? 'text-green-400' : 'text-red-400'}`}>{direction}</p>
            </div>
            <div className="flex-1 text-right">
              <span className="text-gray-500 text-xs">Duration</span>
              <p className="text-white text-sm font-medium">{getDurationText()}</p>
            </div>
          </div>
          
          {/* Row 3: Investment & ROI */}
          <div className="flex justify-between py-2 border-b border-gray-700/50">
            <div className="flex-1">
              <span className="text-gray-500 text-xs">Investment</span>
              <p className="text-white text-sm font-medium">{formatMoney(investmentAmount)}</p>
            </div>
            <div className="flex-1 text-right">
              <span className="text-gray-500 text-xs">ROI</span>
              <p className="text-green-400 text-sm font-bold">{profitRate.toFixed(0)}%</p>
            </div>
          </div>
          
          {/* Row 4: Profit/Loss & Total Credited */}
          <div className="flex justify-between py-2 border-b border-gray-700/50">
            <div className="flex-1">
              <span className="text-gray-500 text-xs">Profit/Loss</span>
              <p className={`text-sm font-bold ${isWin ? 'text-green-400' : 'text-red-400'}`}>
                {isWin ? '+' : '-'}{formatMoney(Math.abs(profitLoss))}
              </p>
            </div>
            <div className="flex-1 text-right">
              <span className="text-gray-500 text-xs">Total Credited</span>
              <p className="text-white text-sm font-medium">{formatMoney(totalCredited)}</p>
            </div>
          </div>
          
          {/* Row 5: Start & Expiry Time */}
          <div className="flex justify-between py-2 border-b border-gray-700/50">
            <div className="flex-1">
              <span className="text-gray-500 text-xs">Start Time</span>
              <p className="text-white text-xs">{formatDate(summary.start_time || trade.start_time || trade.created_at)}</p>
            </div>
            <div className="flex-1 text-right">
              <span className="text-gray-500 text-xs">Expiry Time</span>
              <p className="text-white text-xs">{formatDate(summary.end_time || trade.end_time)}</p>
            </div>
          </div>
          
          {/* Row 6: Completed At */}
          <div className="flex justify-between py-2 border-b border-gray-700/50">
            <div className="flex-1">
              <span className="text-gray-500 text-xs">Completed At</span>
              <p className="text-white text-xs">{formatDate(summary.settled_at || trade.result_time || trade.updated_at)}</p>
            </div>
            {(summary.admin_controlled || trade.admin_controlled) && (
              <div className="flex-1 text-right">
                <span className="text-gray-500 text-xs">Trade Mode</span>
                <p className="text-yellow-400 text-xs font-medium">🎛️ {summary.trade_mode_applied || 'Admin'}</p>
              </div>
            )}
          </div>
        </div>
        
        {/* Close Button - Compact */}
        <div className="p-3">
          <button
            onClick={onClose}
            className="w-full py-3 bg-gray-800 hover:bg-gray-700 text-white font-bold rounded-xl transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const EnhancedHistoryView = () => {
  const [orderHistory, setOrderHistory] = useState([]);
  const [arbitrageHistory, setArbitrageHistory] = useState([]);
  const [binaryHistory, setBinaryHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTrade, setSelectedTrade] = useState(null); // For detail modal
  
  // Filters
  const [filterType, setFilterType] = useState('all'); // all, arbitrage, binary, orders
  const [filterStatus, setFilterStatus] = useState('all'); // all, won, lost, completed, cancelled
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState('all'); // all, today, week, month

  const fetchAllHistory = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const [orders, investments, trades] = await Promise.all([
        tradingAPI.getOrderHistory().catch(() => []),
        arbitrageAPI.getInvestmentHistory().catch(() => []),
        binaryAPI.getTradeHistory(200).catch(() => []),
      ]);
      setOrderHistory(orders);
      setArbitrageHistory(investments);
      setBinaryHistory(trades);
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllHistory();
    // Refresh the moment any trade ends, plus a 15s safety poll
    const onSettled = () => fetchAllHistory(true);
    window.addEventListener('phantomtrades:trade-settled', onSettled);
    const iv = setInterval(() => fetchAllHistory(true), 15000);
    return () => {
      window.removeEventListener('phantomtrades:trade-settled', onSettled);
      clearInterval(iv);
    };
  }, []);

  const isJustSettled = (item) => {
    const t = new Date(item.result_time || item.settlement_time || 0).getTime();
    return Date.now() - t < 2 * 60 * 1000;
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch {
      return dateString;
    }
  };

  // Calculate statistics
  const calculateStats = () => {
    const binaryWins = binaryHistory.filter(t => t.status === 'won').length;
    const binaryTotal = binaryHistory.length;
    const binaryWinRate = binaryTotal > 0 ? ((binaryWins / binaryTotal) * 100).toFixed(1) : 0;
    
    const totalProfit = [
      ...arbitrageHistory.map(inv => inv.expected_return || 0),
      ...binaryHistory.map(trade => trade.actual_profit || 0)
    ].reduce((sum, val) => sum + val, 0);
    
    const totalTrades = binaryTotal + arbitrageHistory.length + orderHistory.length;
    
    return {
      totalTrades,
      binaryWinRate,
      totalProfit,
      avgProfit: totalTrades > 0 ? totalProfit / totalTrades : 0
    };
  };

  // Filter data based on selected filters
  const getFilteredData = () => {
    let data = [];
    
    // Add data based on type filter
    if (filterType === 'all' || filterType === 'arbitrage') {
      data = [...data, ...arbitrageHistory.map(item => ({ ...item, type: 'arbitrage' }))];
    }
    if (filterType === 'all' || filterType === 'binary') {
      data = [...data, ...binaryHistory.map(item => ({ ...item, type: 'binary' }))];
    }
    if (filterType === 'all' || filterType === 'orders') {
      data = [...data, ...orderHistory.map(item => ({ ...item, type: 'order' }))];
    }
    
    // Filter by status
    if (filterStatus !== 'all') {
      data = data.filter(item => item.status === filterStatus);
    }
    
    // Filter by search term
    if (searchTerm) {
      data = data.filter(item => {
        const searchLower = searchTerm.toLowerCase();
        return (
          item.crypto_symbol?.toLowerCase().includes(searchLower) ||
          item.package_name?.toLowerCase().includes(searchLower) ||
          item.pair?.toLowerCase().includes(searchLower)
        );
      });
    }
    
    // Filter by date range
    if (dateRange !== 'all') {
      const now = new Date();
      data = data.filter(item => {
        const itemDate = new Date(item.updated_at || item.result_time || item.completed_at);
        const diffDays = (now - itemDate) / (1000 * 60 * 60 * 24);
        
        if (dateRange === 'today') return diffDays < 1;
        if (dateRange === 'week') return diffDays < 7;
        if (dateRange === 'month') return diffDays < 30;
        return true;
      });
    }
    
    // Sort by date (newest first)
    data.sort((a, b) => {
      const dateA = new Date(a.updated_at || a.result_time || a.completed_at);
      const dateB = new Date(b.updated_at || b.result_time || b.completed_at);
      return dateB - dateA;
    });
    
    return data;
  };

  const stats = calculateStats();
  const filteredData = getFilteredData();

  if (loading) {
    return (
      <div className="glow-card p-6 rounded-xl">
        <div className="text-center py-12">
          <div className="animate-spin h-12 w-12 border-4 border-purple-500 border-t-transparent rounded-full mx-auto"></div>
          <p className="text-gray-400 mt-4">Loading history...</p>
        </div>
      </div>
    );
  }

  const totalHistory = orderHistory.length + arbitrageHistory.length + binaryHistory.length;

  if (totalHistory === 0) {
    return (
      <div className="glow-card p-6 rounded-xl">
        <div className="text-center py-12">
          <HistoryIcon className="h-16 w-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 text-lg">No history</p>
          <p className="text-gray-500 text-sm mt-2">Your completed trades and investments will appear here</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Statistics Overview */}
      <div className="glow-card p-4 sm:p-6 rounded-xl bg-gradient-to-br from-purple-900/50 to-blue-900/50">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center">
          <BarChart2 className="h-5 w-5 mr-2 text-purple-400" />
          Trading Performance
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-black/30 rounded-lg p-3">
            <div className="text-xs text-gray-400 mb-1">Total Trades</div>
            <div className="text-2xl font-bold text-white">{stats.totalTrades}</div>
          </div>
          <div className="bg-black/30 rounded-lg p-3">
            <div className="text-xs text-gray-400 mb-1">Win Rate</div>
            <div className="text-2xl font-bold text-green-400">{stats.binaryWinRate}%</div>
          </div>
          <div className="bg-black/30 rounded-lg p-3">
            <div className="text-xs text-gray-400 mb-1">Total Profit</div>
            <div className={`text-2xl font-bold ${stats.totalProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {stats.totalProfit >= 0 ? '+' : ''}{fmtUsd(stats.totalProfit)}
            </div>
          </div>
          <div className="bg-black/30 rounded-lg p-3">
            <div className="text-xs text-gray-400 mb-1">Avg Profit</div>
            <div className={`text-2xl font-bold ${stats.avgProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {stats.avgProfit >= 0 ? '+' : ''}{fmtUsd(stats.avgProfit)}
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="glow-card p-4 rounded-xl">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-sm font-bold text-white flex items-center">
            <Filter className="h-4 w-4 mr-2" />
            Filters
          </h4>
          <button
            onClick={() => {
              setFilterType('all');
              setFilterStatus('all');
              setSearchTerm('');
              setDateRange('all');
            }}
            className="text-xs text-purple-400 hover:text-purple-300"
          >
            Clear All
          </button>
        </div>
        
        <div className="space-y-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by symbol or package..."
              className="w-full pl-10 pr-4 py-2 bg-gray-800 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
            />
          </div>

          {/* Filter Buttons */}
          <div className="flex flex-wrap gap-2">
            {/* Type Filter */}
            <div className="flex gap-2">
              {['all', 'binary', 'arbitrage', 'orders'].map((type) => (
                <button
                  key={type}
                  onClick={() => setFilterType(type)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    filterType === type
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                  }`}
                >
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </button>
              ))}
            </div>
            
            {/* Status Filter */}
            <div className="flex gap-2">
              {['all', 'won', 'lost', 'completed'].map((status) => (
                <button
                  key={status}
                  onClick={() => setFilterStatus(status)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    filterStatus === status
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                  }`}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
            </div>
            
            {/* Date Filter */}
            <div className="flex gap-2">
              {[
                { value: 'all', label: 'All Time' },
                { value: 'today', label: 'Today' },
                { value: 'week', label: 'Week' },
                { value: 'month', label: 'Month' }
              ].map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => setDateRange(value)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center ${
                    dateRange === value
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                  }`}
                >
                  {value !== 'all' && <Calendar className="h-3 w-3 mr-1" />}
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Results Count */}
      <div className="flex items-center justify-between px-2">
        <p className="text-sm text-gray-400">
          Showing {filteredData.length} of {totalHistory} trades
        </p>
        {filteredData.length > 0 && (
          <button className="text-sm text-purple-400 hover:text-purple-300 flex items-center">
            <Download className="h-4 w-4 mr-1" />
            Export
          </button>
        )}
      </div>

      {/* Filtered Results */}
      {filteredData.length === 0 ? (
        <div className="glow-card p-6 rounded-xl">
          <div className="text-center py-8">
            <Filter className="h-12 w-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">No results match your filters</p>
            <p className="text-gray-500 text-sm mt-1">Try adjusting your search criteria</p>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredData.map((item, index) => {
            // Render based on type
            if (item.type === 'arbitrage') {
              return (
                <div 
                  key={`arb-${index}`} 
                  className="glow-card p-4 rounded-xl hover:bg-gray-800/70 transition-colors border-l-4 border-yellow-500 cursor-pointer group"
                  onClick={() => setSelectedTrade(item)}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <Zap className="h-5 w-5 text-yellow-400" />
                      <span className="px-3 py-1 rounded-full text-xs font-bold bg-yellow-500/20 text-yellow-400">
                        {(item.package_name || 'STANDARD').toUpperCase()}
                      </span>
                      <span className="text-white font-bold text-lg">${(item.amount || 0).toLocaleString()}</span>
                      <span className={`text-xs px-2 py-1 rounded ${
                        item.status === 'completed' ? 'bg-green-500/20 text-green-400' : 'bg-gray-700 text-gray-400'
                      }`}>
                        {(item.status || 'pending').toUpperCase()}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-gray-500">{formatDate(item.updated_at)}</span>
                      <Info className="h-4 w-4 text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-4 text-sm bg-black/20 rounded-lg p-3">
                    <div>
                      <div className="text-gray-500 text-xs mb-1">Period</div>
                      <div className="text-white font-semibold">{item.period_days || 0} days</div>
                    </div>
                    <div>
                      <div className="text-gray-500 text-xs mb-1">ROI</div>
                      <div className="text-white font-semibold">{((item.profit_rate || 0) * 100).toFixed(1)}%</div>
                    </div>
                    <div>
                      <div className="text-gray-500 text-xs mb-1">Profit</div>
                      <div className="text-green-400 font-semibold">+${fmtFixed((item.expected_return || 0), 2)}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-gray-500 text-xs mb-1">Payout</div>
                      <div className="text-yellow-400 font-semibold">${fmtFixed((item.total_payout || 0), 2)}</div>
                    </div>
                  </div>
                  {/* Details Button */}
                  <div className="flex items-center justify-end mt-3 pt-3 border-t border-gray-700/50">
                    <button
                      onClick={(e) => { e.stopPropagation(); setSelectedTrade(item); }}
                      className="px-4 py-1.5 bg-yellow-600 hover:bg-yellow-700 rounded-lg text-white text-xs font-bold flex items-center space-x-1 transition-colors"
                    >
                      <BarChart2 className="h-3 w-3" />
                      <span>Details</span>
                    </button>
                  </div>
                </div>
              );
            }
            
            if (item.type === 'binary') {
              const isWin = item.status === 'won';
              return (
                <div 
                  key={`bin-${index}`} 
                  data-testid={`history-binary-${item.id}`}
                  className={`glow-card p-4 rounded-xl hover:bg-gray-800/70 transition-colors border-l-4 cursor-pointer group ${
                    isWin ? 'border-green-500' : 'border-red-500'
                  }`}
                  onClick={() => setSelectedTrade(item)}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      {item.prediction === 'up' ? (
                        <TrendingUp className="h-5 w-5 text-green-400" />
                      ) : (
                        <TrendingDown className="h-5 w-5 text-red-400" />
                      )}
                      {getCommodity(item.crypto_symbol) && <CryptoIcon symbol={item.crypto_symbol} size={24} />}
                      <span className="text-white font-bold text-lg">{formatPair(item.crypto_symbol || 'BTC')}</span>
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                        item.prediction === 'up' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                      }`}>
                        {(item.prediction || 'up').toUpperCase()}
                      </span>
                      <span className={`text-xs px-2 py-1 rounded font-bold ${
                        isWin ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                      }`}>
                        {(item.status || 'pending').toUpperCase()}
                      </span>
                      {isJustSettled(item) && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-purple-500/20 text-purple-300 animate-pulse" data-testid={`history-just-settled-${item.id}`}>JUST SETTLED</span>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-gray-500">{formatDate(item.result_time)}</span>
                      <Info className="h-4 w-4 text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-4 text-sm bg-black/20 rounded-lg p-3">
                    <div>
                      <div className="text-gray-500 text-xs mb-1">Investment</div>
                      <div className="text-white font-semibold">${fmtFixed((item.amount || 0), 2)}</div>
                    </div>
                    <div>
                      <div className="text-gray-500 text-xs mb-1">Entry / Exit</div>
                      <div className="text-white font-semibold text-xs">
                        ${fmtPrice(item.entry_price || 0)} / {item.exit_price != null ? `$${fmtPrice(item.exit_price)}` : 'N/A'}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-500 text-xs mb-1">Duration</div>
                      <div className="text-white font-semibold">{item.duration_seconds || 0}s</div>
                    </div>
                    <div className="text-right">
                      <div className="text-gray-500 text-xs mb-1">Result</div>
                      <div className={`font-bold ${
                        (item.actual_profit || 0) > 0 ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {(item.actual_profit || 0) > 0 ? '+' : ''}{fmtUsd(item.actual_profit || 0)}
                      </div>
                    </div>
                  </div>
                  {/* Details Button */}
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-700/50">
                    <div className="flex items-center space-x-2">
                      {item.summary_report && (
                        <span className="text-xs px-2 py-1 bg-purple-500/20 text-purple-400 rounded-full">
                          📊 Summary Available
                        </span>
                      )}
                      {item.admin_controlled && (
                        <span className="text-xs px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded-full">
                          🎛️ Admin Controlled
                        </span>
                      )}
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); setSelectedTrade(item); }}
                      className="px-4 py-1.5 bg-purple-600 hover:bg-purple-700 rounded-lg text-white text-xs font-bold flex items-center space-x-1 transition-colors"
                    >
                      <BarChart2 className="h-3 w-3" />
                      <span>Details</span>
                    </button>
                  </div>
                </div>
              );
            }
            
            // Regular orders
            return (
              <div 
                key={`ord-${index}`} 
                className="glow-card p-4 rounded-xl hover:bg-gray-800/70 transition-colors border-l-4 border-purple-500 cursor-pointer group"
                onClick={() => setSelectedTrade(item)}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <Activity className="h-5 w-5 text-purple-400" />
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                      item.side === 'buy' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                    }`}>
                      {(item.side || 'buy').toUpperCase()}
                    </span>
                    <span className="text-white font-bold text-lg">{item.pair || 'BTC/USDT'}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-500">{formatDate(item.completed_at || item.updated_at)}</span>
                    <Info className="h-4 w-4 text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-4 text-sm bg-black/20 rounded-lg p-3">
                  <div>
                    <div className="text-gray-500 text-xs mb-1">Amount</div>
                    <div className="text-white font-semibold">{item.filled_amount || item.amount || 0}</div>
                  </div>
                  <div>
                    <div className="text-gray-500 text-xs mb-1">Price</div>
                    <div className="text-white font-semibold">{(item.executed_price ?? item.price) != null ? `$${fmtFixed(item.executed_price ?? item.price, 2)}` : 'N/A'}</div>
                  </div>
                  <div>
                    <div className="text-gray-500 text-xs mb-1">Total</div>
                    <div className="text-yellow-400 font-semibold">${fmtFixed(item.total, 2)}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-gray-500 text-xs mb-1">Status</div>
                    <div className={`font-semibold capitalize text-xs ${
                      item.status === 'completed' ? 'text-green-400' : 
                      item.status === 'cancelled' ? 'text-yellow-400' : 
                      'text-red-400'
                    }`}>
                      {item.status}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      
      {/* Trade Detail Modal */}
      {selectedTrade && (
        <TradeDetailModal 
          trade={selectedTrade} 
          onClose={() => setSelectedTrade(null)} 
        />
      )}
    </div>
  );
};

export default EnhancedHistoryView;
